<template>
  <v-card class="elevation-0" data-cy="uatDashboard__widget--memberOrgTestingStatus">
    <v-select
      v-model="status"
      :items="statusList"
      label="Testing Status"
      @change="handleFilterChange($event)"
      data-cy="uatDashboard__memberOrgStatusSelector"
    ></v-select>
    <v-data-table
      :footer-props="footerProps"
      :headers="headers"
      :items="testPlanList"
      :options.sync="options"
      @update:options="updateRowsPerPageConfig"
      data-cy="uatDashboard__memberOrgStatusTable"
      item-key="releaseNoteId"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>
            <router-link :to="`/pack/${packId}/releaseNote/${item.releaseNoteId}/testPlan/${serviceAreaId}/view`">
              {{ item.releaseNoteId }}
            </router-link>
          </td>
          <td>{{ truncate(item.title, 200) }}</td>
          <td class="och-uatDashboardTable__cell--summary">
            <v-tooltip v-if="item.summary" bottom max-width="420">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">{{ truncate(stripHtmlTags(item.summary), 200) }}</div>
              </template>
              <span v-html="item.summary"></span>
            </v-tooltip>
          </td>
          <td v-if="status !== 'implementLater'">
            <a :href="`https://jira.ochin.org/browse/${item.uatJira}`" target="_blank" rel="noopener noreferrer" title="Opens in new tab">{{ item.uatJira }}</a>
          </td>
          <td v-if="status === 'implementLater'">{{ item.implementationStatus }}</td>
          <td v-if="status === 'failedQuestion'">{{ item.reportedComments || "--" }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import testPlanApi from "@/api/testPlan";
import { truncate, stripHtmlTags } from "@/lib/summaryFormatters";

export default {
  name: "dashboardTableComponent",
  computed: {
    packId() {
      return this.$route.params.packId;
    },
    rowsPerPage() {
      return this.$store.state.session.user.userConfiguration.widgets.uatDashboardMemberOrg.rowsPerPage;
    },
    serviceAreaId() {
      return this.$store.state.session.serviceArea;
    },
    testingStatusFilter() {
      return this.$store.state.session.user.userConfiguration.widgets.uatDashboardMemberOrg.filters.testingStatus;
    },
    userConfig() {
      return this.$store.state.session.user.userConfiguration;
    },
  },
  created() {
    this.options.itemsPerPage = this.rowsPerPage;
    this.status = this.testingStatusFilter;
    this.updateTestPlanList();
  },
  data() {
    return {
      footerProps: {
        "items-per-page-options": [5, 10, 25, 50],
      },
      status: "failedQuestion",
      statusList: [
        { text: "Failed/Question", value: "failedQuestion" },
        { text: "Not Started", value: "notStarted" },
        { text: "Implement Later", value: "implementLater" },
      ],
      headers: [
        { text: "Release Note ID", value: "releaseNoteId" },
        { text: "Title", value: "title" },
        { text: "Summary", value: "summary" },
        { text: "UAT Jira", value: "uatJira" },
      ],
      testPlanList: [],
      options: {
        page: 1,
        itemsPerPage: 5,
      },
    };
  },
  methods: {
    truncate,
    stripHtmlTags,
    async handleFilterChange(val) {
      await this.updateTestingStatusFilterConfig(val);
      await this.updateTestPlanList();
    },
    setHeaders(val) {
      if (val === "implementLater") {
        this.headers = [
          { text: "Release Note ID", value: "releaseNoteId" },
          { text: "Title", value: "title" },
          { text: "Summary", value: "summary" },
          { text: "Implementation Status", value: "implementationStatus" },
        ];
      } else if (val === "failedQuestion") {
        this.headers = [
          { text: "Release Note ID", value: "releaseNoteId" },
          { text: "Title", value: "title" },
          { text: "Summary", value: "summary" },
          { text: "UAT Jira", value: "uatJira" },
          { text: "Reported Comments", value: "reportedComments" },
        ];
      } else {
        this.headers = [
          { text: "Release Note ID", value: "releaseNoteId" },
          { text: "Title", value: "title" },
          { text: "Summary", value: "summary" },
          { text: "UAT Jira", value: "uatJira" },
        ];
      }
    },
    async updateRowsPerPageConfig(val) {
      // The user config saves to "rowsPerPage", which is what "itemsPerPage" was called in Vuetify v1.
      this.options.itemsPerPage = val.itemsPerPage;
      const widgets = this.userConfig.widgets;
      widgets.uatDashboardMemberOrg.rowsPerPage = val.itemsPerPage;
      try {
        await this.$store.dispatch("session/updateUserConfiguration", {
          widgets,
        });
      } catch (error) {
        console.error(error);
        console.error("Error updating user configuration (rows per page config)");
      }
    },
    async updateTestingStatusFilterConfig(val) {
      const widgets = this.userConfig.widgets;
      widgets.uatDashboardMemberOrg.filters.testingStatus = val;

      try {
        await this.$store.dispatch("session/updateUserConfiguration", {
          widgets,
        });
      } catch (error) {
        console.error(error);
        console.error("Error updating user configuration (testing status filter config)");
      }
    },
    async updateTestPlanList() {
      const { packId, serviceAreaId } = this;

      const statement = {
        operator: "AND",
        list: [
          {
            comparator: "=",
            term: "packId",
            value: packId,
          },
          {
            comparator: "=",
            term: "serviceAreaId",
            value: serviceAreaId,
          },
        ],
      };

      if (this.status === "notStarted") {
        statement.list.push({
          operator: "OR",
          list: [
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Not Started",
            },
          ],
        });
      }

      if (this.status === "failedQuestion") {
        statement.list.push({
          operator: "OR",
          list: [
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Failed",
            },
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Question",
            },
          ],
        });
      }

      if (this.status === "implementLater") {
        statement.list.push({
          operator: "OR",
          list: [
            {
              comparator: "=",
              term: "memberTestPlan.status",
              value: "Implement Later",
            },
          ],
        });
      }

      this.setHeaders(this.status);

      const response = await testPlanApi.search(statement);

      this.testPlanList = response.data.result;
    },
  },
  watch: {
    packId() {
      this.updateTestPlanList();
    },
  },
};
</script>

<style>
.och-uatDashboardTable__cell--summary {
  max-width: 35rem;
  min-width: 25rem;
}
</style>
